import httpMethod from 'services/update2025/httpMethod';
import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";

import { ConfigContext } from "components/config/ConfigContext";
import { createConfig, encrypt, updateConfig, validateConfig } from "services/ConfigService";
import { actionUpdate, actionView } from "utils/action";
import { handleError } from "utils/handleError";

export const ConfigEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["config", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const configContext = useContext(ConfigContext);
    const { config, resetConfig, setConfig, configs, setConfigs, submitted, setSubmitted, configDialog, setConfigDialog } = configContext;
    const [validForm, setValidForm] = useState(false);
    const [titleEditor, setTitleEditor] = useState(t("config.titleCreateDetail"));

    useEffect(() => {
        // create
        if (!config?.id) {
            setTitleEditor(t("config.titleCreateDetail"));
        } else {
            // update
            if (actionUpdate === config?.action) {
                setTitleEditor(t("config.titleUpdateDetail"));
            }
            // view
            else {
                setTitleEditor(t("config.titleDetail"));
            }
        }
    });

    const onInputChange = (e, name) => {
        const val = e.target?.value || "";
        let _config = { ...config };
        _config[`${name}`] = val;

        setConfig(_config);
    };

    const onEnabledChange = (e) => {
        let _config = { ...config };
        _config.enabled = e.checked;
        setConfig(_config);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setConfigDialog(false);
    };

    const saveConfigAction = () => {
        setValidForm(true);
        validateConfig(config)
            .then(() => {
                if (!config?.id) {
                    // Creating
                    return createConfig(headers, config).then((res) => {
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.created", { ns: "common", object: t("config") }), life: 3000 });
                        setValidForm(false);
                        setConfigDialog(false);
                        setSubmitted(true);
                        resetConfig();
                    });
                } else {
                    // Updating
                    return updateConfig(headers, config?.id, {
                        value: config?.value,
                        enabled: config?.enabled,
                        description: config?.description,
                        application: config?.application,
                        profile: config?.profile,
                        label: config?.label,
                        key: config?.key,
                    }).then((res) => {
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.updated", { ns: "common", object: t("config") }), life: 3000 });
                        setValidForm(false);
                        setConfigDialog(false);
                        setSubmitted(true);
                        resetConfig();
                    });
                }
            })
            .catch((error) => handleError(toast, t, error));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < configs.length; i++) {
            if (configs[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const onEncryptValue = () => {
        if (config?.value) {
            encrypt(headers, config?.value)
                .then((res) => {
                    setConfig({ ...config, value: `{cipher}${res.data}` });
                })
                .catch((error) => handleError(toast, t, error));
        }
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveConfigAction} />
        </>
    );

    const checkView = () => {
        return actionView === config?.action;
    };

    return (
        <>
            <Toast ref={toast} />

            <Dialog visible={configDialog} style={{ width: "70%" }} header={titleEditor.toUpperCase()} modal className="p-fluid text-center" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="grid">
                                {/* ứng dụng */}
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="application">
                                        {t("application")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="application" value={config?.application} keyfilter={/[^\s]/} maxLength={100} onChange={(e) => onInputChange(e, "application")} required autoFocus readOnly={config?.id} className={classNames({ "p-invalid": submitted && !config?.application })} />
                                    {submitted && !config?.application && <small className="p-invalid">{t("application.required")}.</small>}
                                </div>
                                {/* profile */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="profile">
                                        {t("profile")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="profile" value={config?.profile} maxLength={100} onChange={(e) => onInputChange(e, "profile")} required readOnly={config?.id} className={classNames({ "p-invalid": submitted && !config?.profile })} />
                                    {submitted && !config?.profile && <small className="p-invalid">{t("profile.required")}.</small>}
                                </div>

                                {/* nhãn */}
                                <div className="field  col-12 md:col-3">
                                    <label htmlFor="label">
                                        {t("label")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="label" value={config?.label} maxLength={100} onChange={(e) => onInputChange(e, "label")} required readOnly={config?.id} className={classNames({ "p-invalid": submitted && !config?.label })} />
                                    {submitted && !config?.label && <small className="p-invalid">{t("label.required")}.</small>}
                                </div>
                            </div>
                            <div className="grid">
                                {/* tên cấu hình */}
                                <div className="field col-12 md:col-9">
                                    <label htmlFor="key">
                                        {t("key")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="key" value={config?.key} keyfilter={/[^\s]/} maxLength={200} onChange={(e) => onInputChange(e, "key")} required readOnly={config?.id} className={classNames({ "p-invalid": validForm && !config?.key })} />
                                    {validForm && !config?.key && <small className="p-invalid">{t("key.required")}.</small>}
                                </div>

                                {/* hiệu lực */}
                                <div className="field col-12 md:col-3" style={{ display: "flex", justifyContent: "center", margin: "auto" }}>
                                    <label htmlFor="enabled">{t("enabled")}</label>
                                    <Checkbox id="enabled" checked={config?.enabled} onChange={onEnabledChange} name="enabled" readOnly={checkView()} />
                                </div>
                            </div>
                            <div className="grid">
                                {/* mô tả */}
                                <div className="field col-12">
                                    <label htmlFor="description">{t("description")}</label>
                                    <InputTextarea id="description" value={config?.description || ""} maxLength={1000} onChange={(e) => onInputChange(e, "description")} rows={3} cols={20} readOnly={checkView()} />
                                </div>
                            </div>
                            <div className="grid">
                                {/* giá trị */}
                                <div className="field col-12 md:col-9">
                                    <label htmlFor="value">
                                        {t("value")} <span className="text-danger">*</span>
                                    </label>
                                    <InputTextarea id="value" value={config?.value} maxLength={1000} onChange={(e) => onInputChange(e, "value")} required className={classNames({ "p-invalid": validForm && !config?.value })} rows={3} cols={20} readOnly={checkView()} />
                                    {validForm && !config?.value && <small className="p-invalid">{t("value.required")}.</small>}
                                </div>
                                <div className="field col-12 md:col-3" style={{ display: "flex", justifyContent: "center", margin: "auto" }}>
                                    <Button label={t("encrypt")} icon="pi pi-replay" disabled={checkView()} className="p-button w-10rem" onClick={onEncryptValue} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12">
                        {/* save */}
                        {!checkView() && (
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                label={t("save", { ns: "common" })}
                                icon="pi pi-check"
                                onClick={saveConfigAction}
                            />
                        )}
                        {/* close */}
                        <Button
                            style={{
                                width: "max-content",
                            }}
                            className="mr-2 p-button-danger"
                            label={t("close", { ns: "common" })}
                            icon="pi pi-times"
                            onClick={hideDialog}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};
